import React, { useEffect, useState } from "react";
import { fetchAPI } from "../../config/api";

const DIVISIONS = [
  { label: "1", value: "d1" },
  { label: "2", value: "d2" },
  { label: "3", value: "d3" },
];
const IMAGE_FALLBACK =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='84'><rect width='100%25' height='100%25' fill='%23f1f5f9'/><path d='M8 64l24-24 14 14 18-20 28 30z' fill='%2394a3b8'/></svg>";

const formatPubDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date
    .toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    .toUpperCase();
};

export const NewsFeed = () => {
  const [division, setDivision] = useState("d1");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    const loadNews = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchAPI(
          `/api/news?sport=baseball&division=${division}`,
          { signal: controller.signal }
        );
        setFeed(data);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setError(err?.message || "Failed to load NCAA news");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadNews();
    return () => controller.abort();
  }, [division]);

  const items = feed?.items || [];

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden animate-pulse">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
          <div className="h-3 w-12 bg-slate-200 rounded" />
          <div className="h-6 w-16 bg-slate-200 rounded-lg" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex gap-3 p-2.5 rounded-lg border border-slate-200">
              <div className="w-16 h-12 bg-slate-200 rounded shrink-0" />
              <div className="min-w-0 flex-1 space-y-2 py-0.5">
                <div className="h-2.5 bg-slate-200 rounded w-1/3" />
                <div className="h-3 bg-slate-200 rounded w-full" />
                <div className="h-3 bg-slate-200 rounded w-4/5" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <label htmlFor="news-division" className="text-xs text-slate-600">
            Division:
          </label>
          <select
            id="news-division"
            value={division}
            onChange={(e) => setDivision(e.target.value)}
            className="w-20 px-2 py-1 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white"
          >
            {DIVISIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!loading && error && (
        <div className="text-sm text-red-600 py-6 text-center">{error}</div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="text-sm text-slate-500 py-6 text-center">
          No news available right now.
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3">
          {items.map((item) => (
            <a
              key={item.link}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex gap-3 p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
            >
              <img
                src={item.image || item.enclosure || IMAGE_FALLBACK}
                alt={item.title}
                className="w-16 h-12 border border-slate-300 object-cover bg-slate-100 shrink-0"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = IMAGE_FALLBACK;
                }}
              />
              <div className="min-w-0">
                <p className="text-[11px] tracking-[0.08em] text-slate-400 mb-0.5">
                  {formatPubDate(item.pubDate)}
                </p>
                <h3 className="text-sm leading-snug font-semibold text-slate-900 group-hover:text-blue-700 transition-colors">
                  {item.title}
                </h3>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
